import {I18n} from '@aws-amplify/core';
import {Text} from '@mantine/core';
import {modals} from '@mantine/modals';
import {AiOutlineStop} from 'react-icons/ai';

import {FlexiButton} from '@flexinet/ui-components';

import {useRetireSyntheticUserMutation} from '../hooks/useSyntheticUsersQuery';
import {useSyntheticUsersSelection} from '../hooks/useSyntheticUsersSelection';

// FNP-969 — RETIRE, and deliberately NOT DeleteRecordsConfirmationButton.
//
// This follows the rule established by accounts/buttons/RemoveRecordButton.tsx:
// the library owns the AFFORDANCE (modal chrome, danger styling, cancel/confirm);
// the feature owns the CONSEQUENCE (what state the record moves to, and whether
// it is reversible). A sentence that would be false if pasted into another module
// belongs in the module.
//
// Every word the shared component would put on screen is wrong here:
//   - its trigger reads "Delete N Selected" — nothing is deleted
//   - its copy says the records will be removed — the row SURVIVES
//
// Retiring tombstones the row (lifecycleStage DELETED / state TOMBSTONED). The
// row must outlive the account it describes, because the roster is the only thing
// distinguishing synthetic activity from organic activity — there is no
// `isSynthetic` flag on Account. Telling an operator this "deletes" the user would
// be false, and would invite them to expect the row to disappear from the list.
// It does not disappear; it changes state, in place, and stays visible.
//
// The icon is a stop, not a bin, for the same reason.
//
// ⚠️ Do not "fix" this back to the library component, and do not add a hard-delete
// sibling — there is no deleteSyntheticUser mutation on the API to call.
export const RetireRecordButton = () => {

    const retireMutation = useRetireSyntheticUserMutation();

    const {
        selection,
        handleClearSelection,
    } = useSyntheticUsersSelection();

    const recordIds = Object.keys(selection)
                            .filter(id => selection[id] === true);
    const disabled = recordIds.length === 0;

    // Each retire is attempted independently: a rejection on one row must not
    // cancel the rest of the batch the operator confirmed, nor skip the selection
    // clear. The mutation's onError raises a notification per failure, so
    // swallowing the rejection here loses no signal (FNP-956 precedent).
    const handleRetire = async () => {
        for (const id of recordIds) {
            try {
                await retireMutation.mutateAsync(id);
            } catch {
                // notified by the mutation's onError; continue with the rest.
            }
        }
        handleClearSelection();
    };

    const label = disabled
        ? I18n.get('Retire')
        : `${I18n.get('Retire')} (${recordIds.length})`;

    const openConfirmModal = () =>
        modals.openConfirmModal({
            title: I18n.get('Retire selected synthetic users'),
            children: (
                <Text size='sm'>
                    {recordIds.length === 1
                        ? I18n.get('This synthetic user will stop waking and be marked DELETED / TOMBSTONED.')
                        : `${recordIds.length} ${I18n.get('synthetic users will stop waking and be marked DELETED / TOMBSTONED.')}`}
                    {' '}
                    {I18n.get('The row is KEPT as a tombstone and stays in this list — it is not deleted. The roster is what distinguishes synthetic activity from real user activity, so retired rows must remain on record.')}
                </Text>
            ),
            labels: {
                confirm: I18n.get('Retire'),
                cancel: I18n.get('Cancel'),
            },
            confirmProps: {color: 'red'},
            onConfirm: () => {
                void handleRetire();
            },
        });

    return (<FlexiButton
        leftSection={<AiOutlineStop/>}
        disabled={disabled}
        onClick={openConfirmModal}
    >{label}</FlexiButton>);
};
